import {
  Button,
  Checkbox,
  FormError,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from "@simplicity/ui";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { apiFetch } from "../../api";
import type { ExportDefinitionDetail, Project } from "../../configTypes";
import {
  EXPORT_CADENCE_OPTIONS,
  EXPORT_DESTINATION_TYPE_OPTIONS,
  EXPORT_FORMAT_OPTIONS,
} from "../../configTypes";
import { tryParseJson } from "../../utils";
import { Breadcrumbs } from "./Breadcrumbs";
import { SqlTextarea } from "./SqlTextarea";

/** Identity row: name, cadence selector, enabled checkbox. */
function IdentityRow({
  name,
  setName,
  cadence,
  setCadence,
  enabled,
  setEnabled,
}: {
  name: string;
  setName: (v: string) => void;
  cadence: string;
  setCadence: (v: string) => void;
  enabled: boolean;
  setEnabled: (v: boolean) => void;
}) {
  return (
    <div className="mb-4 grid grid-cols-[1fr_160px_auto] gap-4">
      <div>
        <Label htmlFor="ed-name" className="form-label">
          Name
        </Label>
        <Input
          id="ed-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. final_forms"
        />
      </div>
      <div>
        <Label htmlFor="ed-cadence" className="form-label">
          Cadence
        </Label>
        <Select value={cadence} onValueChange={setCadence}>
          <SelectTrigger id="ed-cadence">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {EXPORT_CADENCE_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="flex items-end pb-1">
        <Label className="flex items-center gap-2 text-sm text-foreground">
          <Checkbox checked={enabled} onCheckedChange={(v) => setEnabled(v === true)} />
          Enabled
        </Label>
      </div>
    </div>
  );
}

/** Delivery row: format, destination type, filename template, secret ref. */
function DeliveryRow({
  format,
  setFormat,
  destinationType,
  setDestinationType,
  filenameTemplate,
  setFilenameTemplate,
  secretRef,
  setSecretRef,
}: {
  format: string;
  setFormat: (v: string) => void;
  destinationType: string;
  setDestinationType: (v: string) => void;
  filenameTemplate: string;
  setFilenameTemplate: (v: string) => void;
  secretRef: string;
  setSecretRef: (v: string) => void;
}) {
  return (
    <>
      <div className="mb-4 grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="ed-format" className="form-label">
            Format
          </Label>
          <Select value={format} onValueChange={setFormat}>
            <SelectTrigger id="ed-format">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {EXPORT_FORMAT_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="ed-destination-type" className="form-label">
            Destination type
          </Label>
          <Select value={destinationType} onValueChange={setDestinationType}>
            <SelectTrigger id="ed-destination-type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {EXPORT_DESTINATION_TYPE_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="mb-4 grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="ed-filename" className="form-label">
            Filename template
          </Label>
          <Input
            id="ed-filename"
            value={filenameTemplate}
            onChange={(e) => setFilenameTemplate(e.target.value)}
            placeholder="e.g. final_forms_{date}.csv"
          />
        </div>
        <div>
          <Label htmlFor="ed-secret-ref" className="form-label">
            Secret ref
          </Label>
          <Input
            id="ed-secret-ref"
            value={secretRef}
            onChange={(e) => setSecretRef(e.target.value)}
            placeholder="e.g. final-forms-sftp"
          />
        </div>
      </div>
    </>
  );
}

/** SQL editor + the three JSON config textareas (columnOrder, params, vars). */
function ConfigFieldsBlock({
  sqlTemplate,
  setSqlTemplate,
  columnOrderText,
  setColumnOrderText,
  paramsText,
  setParamsText,
  destinationVarsText,
  setDestinationVarsText,
}: {
  sqlTemplate: string;
  setSqlTemplate: (v: string) => void;
  columnOrderText: string;
  setColumnOrderText: (v: string) => void;
  paramsText: string;
  setParamsText: (v: string) => void;
  destinationVarsText: string;
  setDestinationVarsText: (v: string) => void;
}) {
  return (
    <>
      <div className="mb-4">
        <Label htmlFor="ed-sql" className="form-label">
          SQL Template
        </Label>
        <SqlTextarea id="ed-sql" value={sqlTemplate} onChange={setSqlTemplate} />
      </div>

      <div className="mb-4">
        <Label htmlFor="ed-column-order" className="form-label">
          Column order (JSON array)
        </Label>
        <Textarea
          id="ed-column-order"
          className="min-h-[80px] font-mono"
          value={columnOrderText}
          onChange={(e) => setColumnOrderText(e.target.value)}
          placeholder='["student_id", "name"]'
        />
      </div>

      <div className="mb-4 grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="ed-params" className="form-label">
            Params (JSON)
          </Label>
          <Textarea
            id="ed-params"
            className="min-h-[120px] font-mono"
            value={paramsText}
            onChange={(e) => setParamsText(e.target.value)}
            placeholder="{}"
          />
        </div>
        <div>
          <Label htmlFor="ed-destination-vars" className="form-label">
            Destination vars (JSON)
          </Label>
          <Textarea
            id="ed-destination-vars"
            className="min-h-[120px] font-mono"
            value={destinationVarsText}
            onChange={(e) => setDestinationVarsText(e.target.value)}
            placeholder="{}"
          />
        </div>
      </div>
    </>
  );
}

/** Validate + serialize the three JSON textareas into the POST body's JSON parts. */
function parseJsonFields(
  columnOrderText: string,
  paramsText: string,
  destinationVarsText: string,
):
  | { ok: true; columnOrder: unknown; params: unknown; destinationVars: unknown }
  | { ok: false; error: string } {
  const columnOrderResult = tryParseJson(columnOrderText);
  if (!columnOrderResult.ok || !Array.isArray(columnOrderResult.value)) {
    return { ok: false, error: "Column order must be a JSON array of column names." };
  }
  const paramsResult = tryParseJson(paramsText);
  if (!paramsResult.ok) return { ok: false, error: `Params: ${paramsResult.error}` };
  const destinationVarsResult = tryParseJson(destinationVarsText);
  if (!destinationVarsResult.ok) {
    return { ok: false, error: `Destination vars: ${destinationVarsResult.error}` };
  }
  return {
    ok: true,
    columnOrder: columnOrderResult.value,
    params: paramsResult.value,
    destinationVars: destinationVarsResult.value,
  };
}

/** State + fetch-project + submit wiring for the create form. */
function useCreateExportForm(projectId: string) {
  const navigate = useNavigate();

  const [projectName, setProjectName] = useState("");
  const [name, setName] = useState("");
  const [cadence, setCadence] = useState<string>(EXPORT_CADENCE_OPTIONS[0].value);
  const [enabled, setEnabled] = useState(false);
  const [sqlTemplate, setSqlTemplate] = useState("");
  const [format, setFormat] = useState<string>(EXPORT_FORMAT_OPTIONS[0].value);
  const [destinationType, setDestinationType] = useState<string>(
    EXPORT_DESTINATION_TYPE_OPTIONS[0].value,
  );
  const [filenameTemplate, setFilenameTemplate] = useState("");
  const [secretRef, setSecretRef] = useState("");
  const [columnOrderText, setColumnOrderText] = useState("[]");
  const [paramsText, setParamsText] = useState("{}");
  const [destinationVarsText, setDestinationVarsText] = useState("{}");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!projectId) return;
    void apiFetch<Project>(`/api/projects/${projectId}`).then((p) => setProjectName(p.name));
  }, [projectId]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim() || !sqlTemplate.trim() || !filenameTemplate.trim()) return;

    const json = parseJsonFields(columnOrderText, paramsText, destinationVarsText);
    if (!json.ok) {
      setError(json.error);
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const ed = await apiFetch<ExportDefinitionDetail>("/api/export-definitions", {
        method: "POST",
        body: JSON.stringify({
          projectId,
          name: name.trim(),
          enabled,
          cadence,
          sqlTemplate,
          params: json.params,
          format,
          filenameTemplate: filenameTemplate.trim(),
          columnOrder: json.columnOrder,
          destinationType,
          destinationVars: json.destinationVars,
          secretRef: secretRef.trim(),
        }),
      });
      void navigate(`/config/export-definitions/${ed.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create export definition");
      setSubmitting(false);
    }
  }

  return {
    navigate,
    projectName,
    name,
    setName,
    cadence,
    setCadence,
    enabled,
    setEnabled,
    sqlTemplate,
    setSqlTemplate,
    format,
    setFormat,
    destinationType,
    setDestinationType,
    filenameTemplate,
    setFilenameTemplate,
    secretRef,
    setSecretRef,
    columnOrderText,
    setColumnOrderText,
    paramsText,
    setParamsText,
    destinationVarsText,
    setDestinationVarsText,
    submitting,
    error,
    handleSubmit,
  };
}

export function CreateExportDefinition() {
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get("projectId") ?? "";
  const {
    navigate,
    projectName,
    name,
    setName,
    cadence,
    setCadence,
    enabled,
    setEnabled,
    sqlTemplate,
    setSqlTemplate,
    format,
    setFormat,
    destinationType,
    setDestinationType,
    filenameTemplate,
    setFilenameTemplate,
    secretRef,
    setSecretRef,
    columnOrderText,
    setColumnOrderText,
    paramsText,
    setParamsText,
    destinationVarsText,
    setDestinationVarsText,
    submitting,
    error,
    handleSubmit,
  } = useCreateExportForm(projectId);

  return (
    <div className="mx-auto max-w-4xl">
      <Breadcrumbs
        items={[
          { label: "Projects", to: "/config/projects" },
          { label: projectName || "...", to: `/config/projects/${projectId}` },
          { label: "New Export" },
        ]}
      />

      <div className="form-card">
        <h2 className="mb-4 text-lg text-foreground">New Export Definition</h2>
        {error && (
          <div className="mb-4 rounded bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error}
          </div>
        )}

        <form onSubmit={(e) => void handleSubmit(e)}>
          <IdentityRow
            name={name}
            setName={setName}
            cadence={cadence}
            setCadence={setCadence}
            enabled={enabled}
            setEnabled={setEnabled}
          />

          <DeliveryRow
            format={format}
            setFormat={setFormat}
            destinationType={destinationType}
            setDestinationType={setDestinationType}
            filenameTemplate={filenameTemplate}
            setFilenameTemplate={setFilenameTemplate}
            secretRef={secretRef}
            setSecretRef={setSecretRef}
          />

          <ConfigFieldsBlock
            sqlTemplate={sqlTemplate}
            setSqlTemplate={setSqlTemplate}
            columnOrderText={columnOrderText}
            setColumnOrderText={setColumnOrderText}
            paramsText={paramsText}
            setParamsText={setParamsText}
            destinationVarsText={destinationVarsText}
            setDestinationVarsText={setDestinationVarsText}
          />

          {!projectId && <FormError>Missing projectId — open this form from a project.</FormError>}

          <div className="form-actions">
            <Button
              type="button"
              variant="outline"
              onClick={() => void navigate(`/config/projects/${projectId}`)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={
                submitting || !name.trim() || !sqlTemplate.trim() || !filenameTemplate.trim()
              }
            >
              {submitting ? "Creating..." : "Create"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
